import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { cleanupAuthState } from "@/utils/auth-utils";
import { useQueryClient } from "@tanstack/react-query";
import { clearGymNotificationFlag } from "@/hooks/sessionStorage";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log("🔧 AuthProvider: Setting up centralized auth management");

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("🔄 AuthProvider: Auth state changed:", event, !!currentSession);
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (event === 'SIGNED_OUT') {
          console.log("👋 AuthProvider: User signed out - cleaning up");
          cleanupAuthState();
          sessionStorage.removeItem("hasSeenIntro");
          clearGymNotificationFlag();
          queryClient.clear();
        }
        
        if (event === 'SIGNED_IN' && currentSession?.user) {
          console.log("✅ AuthProvider: User signed in - starting sequential cache refresh:", currentSession.user.id);
          
          // Integrated cache invalidation logic
          const refreshCache = async () => {
            try {
              // Step 1: Clear all cached data
              console.log("🗑️ Clearing all cached queries");
              queryClient.clear();
              
              // Step 2: Small delay to ensure auth state is fully updated
              await new Promise(resolve => setTimeout(resolve, 200));
              
              // Step 3: Force immediate refetch of base queries first
              console.log("🔄 Step 1: Refetching base queries (profile & workout-plans)");
              await Promise.all([
                queryClient.refetchQueries({ queryKey: ['profile'] }),
                queryClient.refetchQueries({ queryKey: ['workout-plans'] })
              ]);
              
              // Step 4: Small delay to let base queries complete
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Step 5: Force refetch of dependent queries
              console.log("🔄 Step 2: Refetching dependent queries");
              await Promise.all([
                queryClient.refetchQueries({ queryKey: ['all-user-routines-with-days'] }),
                queryClient.refetchQueries({ queryKey: ['completed-routines'] }),
                queryClient.refetchQueries({ queryKey: ['current-gym'] })
              ]);
              
              console.log("✅ Sequential cache refresh completed successfully");
            } catch (error) {
              console.error("❌ Error during cache refresh:", error);
              queryClient.invalidateQueries();
            }
          };
          
          // Execute cache refresh asynchronously
          refreshCache();
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session (single call)
    supabase.auth.getUser().then(({ data: { user } }) => {
      console.log("📋 AuthProvider: Initial user check:", !!user);
      setUser(user);
      if (user) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          setSession(session);
        });
      }
      setLoading(false);
    });

    return () => {
      console.log("🧹 AuthProvider: Cleaning up subscription");
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const signOut = async () => {
    try {
      console.log("🚪 AuthProvider: Signing out user...");
      cleanupAuthState();
      
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        console.error("Error during sign out:", err);
      }
      
      setSession(null);
      setUser(null);
      sessionStorage.removeItem("hasSeenIntro");
      clearGymNotificationFlag();
      queryClient.clear();
      
      window.location.href = "/auth";
    } catch (error) {
      console.error("Error during sign out:", error);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    isAuthenticated: !!session,
    signOut
  };

  console.log("📊 AuthProvider state:", { 
    hasSession: !!session, 
    hasUser: !!user,
    loading, 
    isAuthenticated: value.isAuthenticated 
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
